import type { NavEntry, RegistryEntry } from "../types.js";
import { getMockWorkspaceApps, INTERNAL_DEVELOPMENT_WORKSPACE_ID } from "./workspaces.js";

export interface NavGroupItem {
  id: string;
  label: string;
  route: string;
  href: string;
  icon?: string;
}

export interface NavGroup {
  id: string;
  label?: string;
  icon?: string;
  items: NavGroupItem[];
}

const UNGROUPED_ID = "__ungrouped__";

/**
 * Registry classification metadata is optional. Omitted, null and "app" entries
 * are normal app-switcher entries; "legacy" and "static" entries stay in the
 * registry for launcher-style surfaces but are excluded from shell chrome.
 */
export function isAppSwitcherEntry(entry: RegistryEntry): boolean {
  // Optional chaining collapses both undefined and null `type` to undefined,
  // so a single undefined check covers "omitted" and "explicitly null".
  const type = entry.type?.trim().toLowerCase();

  return type === undefined || type === "app";
}

export function selectAppEntries(apps: RegistryEntry[]): RegistryEntry[] {
  return apps.filter(isAppSwitcherEntry);
}

/**
 * Groups nav entries by their group id, preserving first-seen order for both
 * groups and the items inside them. Only the first entry for a group id
 * contributes the group's label and icon, matching the NavEntry contract.
 */
export function toNavGroups(
  entries: NavEntry[],
  resolveHref: (route: string) => string
): NavGroup[] {
  const groups: NavGroup[] = [];
  const groupIndex = new Map<string, NavGroup>();

  for (const entry of entries) {
    const item: NavGroupItem = {
      id: entry.id,
      label: entry.label,
      route: entry.route,
      href: resolveHref(entry.route),
      icon: entry.icon
    };

    const groupId = entry.group ?? UNGROUPED_ID;
    const existing = groupIndex.get(groupId);

    if (existing) {
      existing.items.push(item);
      continue;
    }

    const group: NavGroup = {
      id: groupId,
      label: entry.group ? entry.groupLabel : undefined,
      icon: entry.group ? entry.groupIcon : undefined,
      items: [item]
    };

    groups.push(group);
    groupIndex.set(groupId, group);
  }

  return groups;
}

/**
 * Storage key for an app's collapsed nav groups. Mirrors the key the Lit
 * earlier shell navigation used, so a user's existing collapsed sections carry over.
 */
export function navGroupStorageKey(storageKey: string | null): string | null {
  return storageKey ? `kn-nav-collapsed:${storageKey}` : null;
}

export function parseCollapsedGroups(raw: string | null): Set<string> {
  if (!raw) {
    return new Set();
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    if (!Array.isArray(parsed)) {
      return new Set();
    }

    return new Set(parsed.filter((value): value is string => typeof value === "string"));
  } catch {
    // Corrupted or inaccessible storage  -  start from defaults.
    return new Set();
  }
}

/**
 * Serialises collapsed group ids, dropping any that no longer exist so stale
 * entries don't accumulate. Returns null when nothing is collapsed, signalling
 * the caller to remove the key entirely.
 */
export function serializeCollapsedGroups(
  collapsed: Set<string>,
  knownGroupIds: string[]
): string | null {
  const known = new Set(knownGroupIds);
  const pruned = [...collapsed].filter((id) => known.has(id));

  return pruned.length === 0 ? null : JSON.stringify(pruned);
}

export function toggleCollapsedGroup(collapsed: Set<string>, groupId: string): Set<string> {
  const next = new Set(collapsed);

  if (next.has(groupId)) {
    next.delete(groupId);
  } else {
    next.add(groupId);
  }

  return next;
}

export function partitionNavEntries(entries: NavEntry[]): {
  main: NavEntry[];
  bottom: NavEntry[];
} {
  return {
    main: entries.filter((entry) => entry.position !== "bottom"),
    bottom: entries.filter((entry) => entry.position === "bottom")
  };
}

export interface WorkspaceAppEntry {
  id: string;
  label: string;
  icon: string;
  /** Normalized #RRGGBB from the registry, absent on legacy entries. */
  color?: string;
  /** Fixed token-backed accent used only by disabled workspace mockups. */
  accent?: number;
  href?: string;
  route?: string;
  disabled: boolean;
}

/**
 * Every real app in the current registry belongs to Internal Development.
 * The other workspaces are a visual mockup  -  their apps come from a fixed
 * placeholder list (see workspaces.ts) and render disabled, with no route.
 */
export function resolveWorkspaceApps(
  workspaceId: string,
  registryApps: RegistryEntry[],
  resolveHref: (route: string) => string,
  workspaceApps?: RegistryEntry[]
): WorkspaceAppEntry[] {
  const apps = workspaceApps ?? (workspaceId === INTERNAL_DEVELOPMENT_WORKSPACE_ID ? registryApps : []);

  if (workspaceApps || workspaceId === INTERNAL_DEVELOPMENT_WORKSPACE_ID) {
    return selectAppEntries(apps).map((app) => ({
      id: app.id,
      label: app.navLabel ?? app.id,
      icon: app.icon,
      ...(app.color ? { color: app.color } : {}),
      href: resolveHref(app.route),
      route: app.route,
      disabled: false
    }));
  }

  return getMockWorkspaceApps(workspaceId).map((app) => ({
    id: app.id,
    label: app.label,
    icon: app.icon,
    accent: app.accent,
    disabled: true
  }));
}
